'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { servicesData, type ServiceKey, type CourseEntry } from '@/data/servicesData';

interface ServiceModalProps {
  activeCardId: ServiceKey | null;
  onClose: () => void;
}

type View = 'process' | 'courses' | 'detail';

const ModalIcon = () => (
  <div className="modal-icon">
    <svg viewBox="0 0 24 24">
      <path d="M12 2l3 7h7l-5.5 4.5 2 7L12 16l-6.5 4.5 2-7L2 9h7z" />
    </svg>
  </div>
);

/** Ported from scripts.js banner 13, as a real state machine instead of manual style.display/innerHTML. */
export default function ServiceModal({ activeCardId, onClose }: ServiceModalProps) {
  const [view, setView] = useState<View>('process');
  const [activeCourse, setActiveCourse] = useState<CourseEntry | null>(null);

  const isVisible = activeCardId !== null;
  const data = activeCardId ? servicesData[activeCardId] : null;

  // Resets the view when a new card opens. The modal stays mounted (rather
  // than remounting via a key) so the CSS close transition can still play.
  useEffect(() => {
    if (!data) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setView(data.type === 'courses' ? 'courses' : 'process');
    setActiveCourse(null);
  }, [activeCardId, data]);

  useEffect(() => {
    document.body.style.overflow = isVisible ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isVisible, onClose]);

  return (
    <div
      className={`modal-overlay${isVisible ? ' visible' : ''}`}
      id="service-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-content">
        <button className="modal-close" id="modal-close-btn" onClick={onClose}>
          ✕
        </button>

        {data?.type === 'process' && view === 'process' && (
          <div className="modal-view" id="modal-process-view">
            <div className="modal-header">
              <ModalIcon />
              <h3 id="modal-process-title">{data.title}</h3>
            </div>
            <p className="modal-description" id="modal-process-desc">
              {data.description}
            </p>
            <div className="process-steps-container" id="modal-process-steps">
              {data.steps.map((step, i) => (
                <div className="step-block" key={step}>
                  <div className="step-number">{i + 1}</div>
                  <div className="step-text">{step}</div>
                </div>
              ))}
            </div>
            <Link href="/contacto" className="modal-cta">
              Contactanos
            </Link>
          </div>
        )}

        {data?.type === 'courses' && view === 'courses' && (
          <div className="modal-view" id="modal-courses-view">
            <div className="modal-header">
              <ModalIcon />
              <h3 id="modal-courses-title">{data.title}</h3>
            </div>
            <div className="modal-tag" id="modal-courses-tag">
              {data.tag}
            </div>
            <p className="modal-description" id="modal-courses-desc">
              {data.description}
            </p>
            <ul className="courses-list" id="modal-courses-list">
              {data.courses.map((course) => (
                <li
                  key={course.name}
                  onClick={() => {
                    setActiveCourse(course);
                    setView('detail');
                  }}
                >
                  {course.name}
                  <span>→</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data?.type === 'courses' && view === 'detail' && activeCourse && (
          <div className="modal-view" id="modal-detail-view">
            <button className="modal-back-btn" id="modal-back-btn" onClick={() => setView('courses')}>
              ← Volver
            </button>
            <div className="modal-header">
              <ModalIcon />
              <h3 id="modal-detail-title">{activeCourse.name}</h3>
            </div>
            <div className="modal-tag" id="modal-detail-tag">
              {data.tag}
            </div>
            <p className="modal-description" id="modal-detail-desc">
              {activeCourse.desc}
            </p>
            <div className="modal-goals-box">
              <div className="modal-goals-title">Objetivos del programa</div>
              <ul className="modal-goals-list" id="modal-detail-goals">
                {activeCourse.goals.map((goal) => (
                  <li key={goal}>
                    <span className="goal-check">✓</span> {goal}
                  </li>
                ))}
              </ul>
            </div>
            <Link href="/contacto" className="modal-cta">
              Contactanos
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
